// components/industry-templates/PharmaEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Pill, FlaskConical, Award, Users } from 'lucide-react';

export const PharmaEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Drug Discoveries */}
      {industryData?.drug_discoveries && (
        <Card className="border-0 bg-gradient-to-br from-blue-500/20 to-teal-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Pill className="w-8 h-8" style={{ color: theme.primary }} />
              Latest Drug Discoveries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.drug_discoveries.map((drug: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border-2" style={{ borderColor: theme.primary }}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{drug.name}</h3>
                      <p className="text-slate-300">{drug.company}</p>
                    </div>
                    {drug.breakthrough && (
                      <Badge className="bg-yellow-500 text-black">BREAKTHROUGH</Badge>
                    )}
                  </div>
                  <p className="text-slate-300 text-sm mb-3">{drug.indication}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{drug.phase}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{drug.therapeutic_area}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clinical Trials */}
      {industryData?.clinical_trials && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FlaskConical className="w-6 h-6" style={{ color: theme.primary }} />
              Clinical Trials & Research
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.clinical_trials.map((trial: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{trial.title}</h4>
                  <p className="text-sm text-slate-400 mb-2">{trial.sponsor}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>Phase {trial.phase}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">
                      {trial.participants} participants
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regulatory Updates */}
      {industryData?.regulatory_sessions && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Regulatory Affairs & Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.regulatory_sessions.map((session: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-1">{session.topic}</h4>
                  <p className="text-sm text-slate-400 mb-2">{session.regulatory_body}</p>
                  <Badge style={{ backgroundColor: theme.primary }}>{session.time}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pharma Companies */}
      {industryData?.pharma_companies && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: theme.primary }} />
              Participating Pharma Companies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.pharma_companies.map((company: string, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg text-center border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="text-2xl mb-2">💊</div>
                  <p className="text-white font-medium text-sm">{company}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
